import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { getRefills, createRefill, updateRefillStatus, deleteRefill } from '../../services/api';
import { ClockIcon, PlusIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const DAYS      = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const TODAY_IDX = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;

const STATUS = {
  completed: { label:'Done',    color:'#059669', bg:'#ecfdf5', border:'#a7f3d0' },
  pending:   { label:'Pending', color:'#d97706', bg:'#fef3c7', border:'#fde68a' },
  skipped:   { label:'Skipped', color:'#dc2626', bg:'#fff1f2', border:'#fecdd3' },
};

const RefillSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeDay, setActiveDay] = useState(DAYS[TODAY_IDX]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [form, setForm] = useState({
    fridge_name:'', apartment:'', day: DAYS[TODAY_IDX], time:'10:00', products:''
  });

  const loadRefills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRefills();
      setSchedules(res.data.refills || []);
    } catch {
      toast.error('Failed to load refill schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRefills(); }, [loadRefills]);

  const daySchedules = schedules
    .filter(s => s.day === activeDay)
    .sort((a, b) => a.time.localeCompare(b.time));

  const counts = {
    completed: schedules.filter(s => s.status === 'completed').length,
    pending:   schedules.filter(s => s.status === 'pending').length,
    total:     schedules.length,
  };

  const handleMarkDone = async (s) => {
    const next = s.status === 'completed' ? 'pending' : 'completed';
    try {
      await updateRefillStatus(s.id, next);
      setSchedules(prev => prev.map(item => item.id === s.id ? { ...item, status: next } : item));
      toast.success(next === 'completed' ? '✅ Marked as done!' : 'Marked as pending');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await deleteRefill(id);
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Schedule deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.fridge_name || !form.apartment || !form.time) {
      toast.error('Fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const products = form.products.split(',').map(p => p.trim()).filter(Boolean);
      const res = await createRefill({ ...form, products });
      // Optimistically add to list
      setSchedules(prev => [...prev, {
        id:          res.data.refill_id,
        fridge_name: form.fridge_name,
        apartment:   form.apartment,
        fridge:      form.fridge_name,   // alias for display
        apt:         form.apartment,
        day:         form.day,
        time:        form.time,
        products,
        status:      'pending',
      }]);
      toast.success('Schedule added!');
      setShowModal(false);
      setForm({ fridge_name:'', apartment:'', day: activeDay, time:'10:00', products:'' });
    } catch {
      toast.error('Failed to add schedule');
    } finally {
      setSaving(false);
    }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    .rs-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:2rem; flex-wrap:wrap; gap:1rem; }
    .rs-title   { font-size:1.6rem; font-weight:900; color:#111827; }
    .rs-sub     { font-size:0.88rem; color:#64748b; font-weight:600; margin-top:0.15rem; }

    .rs-add-btn {
      display:flex; align-items:center; gap:0.5rem; padding:0.6rem 1.4rem; border-radius:9999px;
      background:linear-gradient(135deg,#10b981,#059669); color:white; font-weight:800; font-size:0.9rem;
      border:none; cursor:pointer; box-shadow:0 4px 14px rgba(16,185,129,0.3); transition:all 0.3s; font-family:'Nunito',sans-serif;
    }
    .rs-add-btn svg { width:1rem; height:1rem; }
    .rs-add-btn:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(16,185,129,0.35); }

    .rs-summary { display:grid; grid-template-columns:repeat(3,1fr); gap:1.25rem; margin-bottom:2rem; }
    .rs-sum-card { background:white; border-radius:1.5rem; padding:1.25rem 1.5rem; border:1.5px solid; box-shadow:0 4px 12px rgba(0,0,0,0.04); text-align:center; }
    .rs-sum-num   { font-size:2rem; font-weight:900; }
    .rs-sum-label { font-size:0.78rem; font-weight:700; color:#64748b; margin-top:0.2rem; }

    .rs-days { display:flex; gap:0.5rem; margin-bottom:1.75rem; overflow-x:auto; padding-bottom:0.25rem; }
    .rs-day-btn {
      padding:0.5rem 1rem; border-radius:9999px; border:1.5px solid #e5e7eb;
      font-weight:700; font-size:0.82rem; color:#64748b; background:white; cursor:pointer;
      white-space:nowrap; transition:all 0.2s; font-family:'Nunito',sans-serif; flex-shrink:0;
    }
    .rs-day-btn:hover  { border-color:#a7f3d0; color:#059669; }
    .rs-day-btn.active { background:linear-gradient(135deg,#10b981,#059669); color:white; border-color:transparent; box-shadow:0 4px 12px rgba(16,185,129,0.3); }

    .rs-list { display:flex; flex-direction:column; gap:1rem; }
    .rs-item {
      background:white; border-radius:1.25rem; padding:1.25rem 1.5rem;
      border:1.5px solid #f1f5f9; box-shadow:0 4px 12px rgba(0,0,0,0.04);
      display:flex; align-items:center; gap:1.25rem; transition:all 0.3s; flex-wrap:wrap;
    }
    .rs-item:hover     { border-color:#d1fae5; box-shadow:0 8px 20px rgba(5,150,105,0.08); }
    .rs-item.completed { opacity:0.7; }

    .rs-time-box { background:linear-gradient(135deg,#ecfdf5,#e0f2fe); border-radius:0.875rem; padding:0.6rem 0.85rem; text-align:center; flex-shrink:0; min-width:64px; }
    .rs-time-val { font-size:1rem; font-weight:900; color:#059669; }
    .rs-time-lbl { font-size:0.65rem; color:#64748b; font-weight:600; }

    .rs-info         { flex:1; min-width:120px; }
    .rs-fridge-name  { font-weight:800; color:#111827; font-size:0.9rem; }
    .rs-apt-name     { font-size:0.75rem; color:#94a3b8; font-weight:600; margin-top:0.1rem; }
    .rs-products     { display:flex; flex-wrap:wrap; gap:0.4rem; margin-top:0.6rem; }
    .rs-prod-pill    { background:#f0f9ff; color:#0891b2; font-size:0.7rem; font-weight:700; padding:0.15rem 0.6rem; border-radius:9999px; border:1px solid #bae6fd; }

    .rs-item-actions { display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; }
    .rs-status-badge { font-size:0.72rem; font-weight:700; padding:0.25rem 0.65rem; border-radius:9999px; border:1.5px solid; }
    .rs-done-btn {
      display:flex; align-items:center; gap:0.35rem; padding:0.45rem 0.9rem;
      border-radius:9999px; font-weight:700; font-size:0.78rem; border:1.5px solid;
      cursor:pointer; transition:all 0.2s; font-family:'Nunito',sans-serif;
    }
    .rs-done-btn svg      { width:0.85rem; height:0.85rem; }
    .rs-done-btn.pending  { background:#ecfdf5; color:#059669; border-color:#a7f3d0; }
    .rs-done-btn.completed{ background:#f1f5f9; color:#64748b; border-color:#e2e8f0; }
    .rs-done-btn:hover    { filter:brightness(0.92); }

    .rs-del-btn {
      width:32px; height:32px; border-radius:50%; background:#fff1f2; color:#dc2626;
      border:1.5px solid #fecdd3; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all 0.2s;
    }
    .rs-del-btn svg   { width:0.8rem; height:0.8rem; }
    .rs-del-btn:hover { background:#dc2626; color:white; border-color:#dc2626; }

    .rs-empty { padding:3.5rem 2rem; text-align:center; color:#94a3b8; background:white; border-radius:1.5rem; border:1px dashed #e5e7eb; }
    .rs-empty svg { width:2.5rem; height:2.5rem; margin:0 auto 0.75rem; opacity:0.35; }

    /* Modal */
    .rs-modal-bg { position:fixed; inset:0; background:rgba(15,23,42,0.5); display:flex; align-items:center; justify-content:center; z-index:200; padding:1rem; backdrop-filter:blur(4px); }
    .rs-modal    { background:white; border-radius:1.75rem; width:100%; max-width:460px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 25px 60px -10px rgba(0,0,0,0.25); overflow:hidden; }
    .rs-modal-header { background:linear-gradient(135deg,#059669,#0891b2); padding:1.5rem; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
    .rs-modal-header h3 { font-size:1.1rem; font-weight:900; color:white; }
    .rs-modal-close  { width:32px; height:32px; border-radius:50%; background:rgba(255,255,255,0.2); border:none; color:white; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:1rem; }
    .rs-modal-body   { padding:1.5rem; overflow-y:auto; flex:1; }
    .rs-field        { margin-bottom:1.1rem; }
    .rs-label        { display:block; font-size:0.82rem; font-weight:700; color:#374151; margin-bottom:0.45rem; }
    .rs-input, .rs-select {
      width:100%; padding:0.65rem 1rem; border:1.5px solid #e5e7eb; border-radius:0.875rem;
      font-size:0.9rem; font-family:'Nunito',sans-serif; font-weight:600; color:#111827;
      background:#f9fafb; outline:none; transition:all 0.2s;
    }
    .rs-input:focus, .rs-select:focus { border-color:#059669; background:white; box-shadow:0 0 0 3px rgba(5,150,105,0.1); }
    .rs-modal-footer { display:flex; justify-content:flex-end; gap:0.75rem; padding:1.25rem 1.5rem; border-top:1px solid #f1f5f9; flex-shrink:0; background:white; }
    .rs-cancel-btn   { padding:0.6rem 1.4rem; border-radius:9999px; background:#f8fafc; border:1.5px solid #e2e8f0; color:#64748b; font-weight:700; cursor:pointer; font-family:'Nunito',sans-serif; }
    .rs-save-btn     { padding:0.6rem 1.75rem; border-radius:9999px; background:linear-gradient(135deg,#10b981,#059669); color:white; font-weight:800; border:none; cursor:pointer; font-family:'Nunito',sans-serif; transition:all 0.3s; box-shadow:0 4px 14px rgba(16,185,129,0.3); }
    .rs-save-btn:hover    { transform:translateY(-1px); }
    .rs-save-btn:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
    .rs-row2 { display:grid; grid-template-columns:1fr 1fr; gap:0.75rem; }

    .sm-spinner { width:2.5rem; height:2.5rem; border:3px solid #f3f4f6; border-top-color:#059669; border-radius:50%; animation:spin 0.7s linear infinite; margin:4rem auto; }
    @keyframes spin { to{ transform:rotate(360deg); } }
  `;

  if (loading) return (
    <AdminLayout>
      <style>{styles}</style>
      <div className="sm-spinner" />
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{styles}</style>

      <div className="rs-header">
        <div>
          <div className="rs-title">📅 Refill Schedule</div>
          <div className="rs-sub">Manage weekly fridge restocking schedule</div>
        </div>
        <button className="rs-add-btn"
          onClick={() => { setForm({ ...form, day: activeDay }); setShowModal(true); }}>
          <PlusIcon /> Add Schedule
        </button>
      </div>

      {/* Summary */}
      <div className="rs-summary">
        <div className="rs-sum-card" style={{ borderColor:'#a7f3d0' }}>
          <div className="rs-sum-num" style={{ color:'#059669' }}>{counts.completed}</div>
          <div className="rs-sum-label">Completed</div>
        </div>
        <div className="rs-sum-card" style={{ borderColor:'#fde68a' }}>
          <div className="rs-sum-num" style={{ color:'#d97706' }}>{counts.pending}</div>
          <div className="rs-sum-label">Pending</div>
        </div>
        <div className="rs-sum-card" style={{ borderColor:'#bfdbfe' }}>
          <div className="rs-sum-num" style={{ color:'#2563eb' }}>{counts.total}</div>
          <div className="rs-sum-label">Total This Week</div>
        </div>
      </div>

      {/* Day tabs */}
      <div className="rs-days">
        {DAYS.map((day, i) => (
          <button key={day}
            className={`rs-day-btn ${activeDay === day ? 'active' : ''}`}
            onClick={() => setActiveDay(day)}>
            {day.slice(0, 3)}{i === TODAY_IDX ? ' ·' : ''}
          </button>
        ))}
      </div>

      {/* Schedule list */}
      <div className="rs-list">
        {daySchedules.length === 0 ? (
          <div className="rs-empty">
            <ClockIcon />
            <p style={{ fontWeight:700 }}>No schedules for {activeDay}</p>
            <p style={{ fontSize:'0.82rem', marginTop:'0.3rem' }}>Click "Add Schedule" to create one</p>
          </div>
        ) : daySchedules.map(s => {
          const st           = STATUS[s.status] || STATUS.pending;
          const fridgeName   = s.fridge_name || s.fridge || '—';
          const aptName      = s.apartment   || s.apt    || '—';
          const productsList = Array.isArray(s.products) ? s.products : [];
          return (
            <div key={s.id} className={`rs-item ${s.status}`}>
              <div className="rs-time-box">
                <div className="rs-time-val">{s.time?.slice(0,5)}</div>
                <div className="rs-time-lbl">TIME</div>
              </div>
              <div className="rs-info">
                <div className="rs-fridge-name">{fridgeName}</div>
                <div className="rs-apt-name">{aptName}</div>
                <div className="rs-products">
                  {productsList.map(p => (
                    <span key={p} className="rs-prod-pill">{p}</span>
                  ))}
                </div>
              </div>
              <div className="rs-item-actions">
                <span className="rs-status-badge"
                  style={{ color:st.color, background:st.bg, borderColor:st.border }}>
                  {st.label}
                </span>
                <button className={`rs-done-btn ${s.status}`} onClick={() => handleMarkDone(s)}>
                  <CheckCircleIcon />
                  {s.status === 'completed' ? 'Undo' : 'Mark Done'}
                </button>
                <button className="rs-del-btn" onClick={() => handleDelete(s.id)}>
                  <TrashIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="rs-modal-bg" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>📅 Add Refill Schedule</h3>
              <button className="rs-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAdd} style={{ display:'contents' }}>
              <div className="rs-modal-body">
                <div className="rs-row2">
                  <div className="rs-field">
                    <label className="rs-label">Fridge ID *</label>
                    <input className="rs-input" placeholder="e.g. Fridge A-101"
                      value={form.fridge_name}
                      onChange={e => setForm({ ...form, fridge_name: e.target.value })} required />
                  </div>
                  <div className="rs-field">
                    <label className="rs-label">Apartment *</label>
                    <input className="rs-input" placeholder="e.g. Sunrise Towers"
                      value={form.apartment}
                      onChange={e => setForm({ ...form, apartment: e.target.value })} required />
                  </div>
                </div>
                <div className="rs-row2">
                  <div className="rs-field">
                    <label className="rs-label">Day *</label>
                    <select className="rs-select" value={form.day}
                      onChange={e => setForm({ ...form, day: e.target.value })}>
                      {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="rs-field">
                    <label className="rs-label">Time *</label>
                    <input className="rs-input" type="time" value={form.time}
                      onChange={e => setForm({ ...form, time: e.target.value })} required />
                  </div>
                </div>
                <div className="rs-field">
                  <label className="rs-label">Products (comma separated)</label>
                  <input className="rs-input" placeholder="e.g. Milk, Eggs, Butter"
                    value={form.products}
                    onChange={e => setForm({ ...form, products: e.target.value })} />
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-cancel-btn"
                  onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="rs-save-btn" disabled={saving}>
                  {saving ? '⏳ Saving…' : '✅ Add Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default RefillSchedule;